import {
  DAMAGE,
  HEALTH,
  T,
  heartIndex,
  asteroidCenter,
  editTargetAt,
  editTargetTile,
  gravityFieldAt,
  inGravityField,
  playerWorldCenter,
  playerWorldPos,
  playerWorldUp,
  rightVector,
  upVector,
  vDist,
  vDot,
  waterAt,
  type PlayerState,
  type ProjectileState,
  type ShipBody,
  type Sim,
  type TileId,
  type Vec2,
} from "@dyson/shared";
import { materialOf, materialSound } from "../audio/materials";
import { Music } from "../audio/Music";
import type { Sfx, SoundName } from "../audio/Sfx";
import type { InputAxes } from "../input/Input";
import type { Renderer } from "../render/Renderer";

/** Distance walked (in blocks) between footfalls: about two and a half steps a second at full speed. */
const STRIDE = 4.8;
/** How far from a sun's surface (blocks) its hum carries. */
const HUM_RANGE = 400;
/** Below this fraction of full health your heart starts pounding. */
const HEARTBEAT_BELOW = 0.3;
/** Seconds between heartbeats at that threshold; they come quicker as health falls. */
const HEARTBEAT_EVERY = 0.85;
/** A living rock's heart beats this often (s), heard within this many blocks of it. */
const ROCK_HEART_EVERY = 1.6;
const ROCK_HEART_REACH = 90;
/** How often (s) to look round for water, suns and wind: they change slowly, and looking isn't free. */
const PROBE_EVERY = 0.25;

/**
 * The game's sounds that follow from what's happening rather than from one
 * message: cannon booms, footsteps, the helm latch, the launch whoosh, the
 * engine loop, the sound of the place you're in, your heartbeat
 * when you're badly hurt, and the music.
 */
export class GameAudio {
  /** Cannonballs already heard, so each volley booms once. */
  private heard = new Set<string>();
  /** When the last hit landed, so the block it knocks off doesn't also make a dig sound. */
  private lastHitAt = 0;
  private stepDistance = 0;
  /** The last block sound, so a piece going down or coming out sounds once. */
  private lastBlock?: { name: string; time: number; at: Vec2 };
  /** Alternates each footfall, so left and right feet sound a touch different. */
  private leftFoot = false;
  private wasPiloting = false;
  private wasLaunched = false;
  private lastHealth?: number;
  /** Seconds until the next heartbeat. */
  private heartIn = 0;
  /** Till the next beat of a living rock's heart nearby. */
  private rockHeartIn = 0;
  private probeIn = 0;
  /** What the last look round found: the levels the place's loops are heading for. */
  private place = { hum: 0, lap: 0, wind: 0 };
  /** How much of each ship is timber, with the block count it was worked out from. */
  private timber = new Map<string, { cells: number; fraction: number }>();
  private readonly musicPlayer: Music;

  constructor(
    private readonly sfx: Sfx,
    private readonly renderer: Renderer,
  ) {
    this.musicPlayer = new Music(sfx);
  }

  /** A cannonball hit something at (x, y). */
  hit(x: number, y: number): void {
    this.lastHitAt = performance.now();
    this.sfx.play("hit", this.renderer.listen({ x, y }), 0.85 + Math.random() * 0.3);
  }

  /**
   * A ship crashed at (x, y), losing `power` m/s. Louder and deeper the harder,
   * measured from where crashes start to do damage (DAMAGE.impactSpeed).
   */
  impact(x: number, y: number, power: number): void {
    // The blocks it knocks off shouldn't each make a dig sound too.
    this.lastHitAt = performance.now();
    const at = this.renderer.listen({ x, y });
    // 0 at the damage threshold, 1 at four times it.
    const hard = Math.max(0, Math.min(1, (power - DAMAGE.impactSpeed) / (DAMAGE.impactSpeed * 3)));
    this.sfx.play("impact", at, 1.1 - 0.4 * hard + (Math.random() - 0.5) * 0.08, 0.55 + 0.45 * hard);
    // A hard one splinters timber as well.
    if (hard > 0.4) this.sfx.play("hit", at, 0.7 + Math.random() * 0.1, hard);
  }

  /** Someone came aboard the ship we're on, at (x, y). Never quiet: a stranger boarding is worth hearing. */
  board(x: number, y: number): void {
    const at = this.renderer.listen({ x, y });
    this.sfx.play("board", { gain: Math.max(0.75, at.gain), pan: at.pan }, 0.95 + Math.random() * 0.1);
  }

  /** A one-off (a grapple, a door, a whale...), placed in the world when `at` is given. */
  play(name: SoundName, at?: { x: number; y: number }): void {
    this.sfx.play(name, at ? this.renderer.listen(at) : undefined, 0.95 + Math.random() * 0.1);
  }

  /**
   * Something from outside (see NIGHT): a rift starting to open (the flare's
   * warning, pitched up thin and wrong), one snuffed out by someone's light,
   * or something pulled back out at dawn (the warning, slowed and low).
   */
  rift(kind: "rift" | "snuffed" | "pulled", x: number, y: number): void {
    const at = this.renderer.listen({ x, y });
    if (kind === "rift") this.sfx.play("flare", at, 1.8 + Math.random() * 0.2, 0.55);
    else if (kind === "snuffed") this.sfx.play("hit", at, 1.6, 0.4);
    else this.sfx.play("flare", at, 0.55, 0.6);
  }

  /** Every frame: the music, by how much danger we're in (0 calm .. 1 deadly). */
  music(danger: number, dt: number): void {
    this.musicPlayer.update(dt, danger);
  }

  /**
   * A block was dug out (`tile` is what it was) or put down, at world point
   * `at`, sounding like what it's made of. A whole piece changes many tiles at
   * once but makes one sound.
   */
  block(dug: boolean, tile: TileId, at: Vec2): void {
    const name = materialSound(dug ? "dig" : "place", materialOf(tile));
    const now = performance.now();
    const last = this.lastBlock;
    if (last && last.name === name && now - last.time < 80 && vDist(last.at, at) < 6) return;
    this.lastBlock = { name, time: now, at };
    this.sfx.play(name, this.renderer.listen(at), 0.9 + Math.random() * 0.2);
  }

  /** True just after a hit: a block removed now was knocked off by it. */
  get justHit(): boolean {
    return performance.now() - this.lastHitAt < 150;
  }

  /** One boom per ship that fired since the last snapshot, where its first shot appeared. */
  cannons(projectiles: ProjectileState[]): void {
    const fired = new Map<string, ProjectileState>();
    for (const p of projectiles) if (!this.heard.has(p.id) && !fired.has(p.ownerShipId)) fired.set(p.ownerShipId, p);
    this.heard = new Set(projectiles.map((p) => p.id));
    for (const p of fired.values()) this.sfx.play("cannon", this.renderer.listen(p.position), 0.9 + Math.random() * 0.2);
  }

  /** Every frame, from our own predicted character and what we're pressing. Returns true if we were hurt since last frame. */
  ambient(me: PlayerState | undefined, axes: InputAxes, sim: Sim | undefined, dt: number): boolean {
    const moving = Math.max(Math.abs(axes.moveX), Math.abs(axes.moveY), Math.abs(axes.turn));
    const piloting = !!me?.piloting;
    if (piloting !== this.wasPiloting) this.sfx.play("helm");
    this.wasPiloting = piloting;
    const launched = !!me?.launched;
    if (launched && !this.wasLaunched) this.sfx.play("launch");
    this.wasLaunched = launched;
    this.sfx.loop("engine", piloting ? 0.15 + 0.45 * moving : 0, dt);

    // Getting hurt thuds.
    const hurt = me !== undefined && this.lastHealth !== undefined && me.health < this.lastHealth - 0.5;
    if (hurt) this.sfx.play("hit", { gain: 0.8, pan: 0 }, 0.6);
    this.lastHealth = me?.health;
    this.heartbeat(me, dt);
    this.rockHeart(me, sim, dt);

    if (me && sim && !piloting) {
      // Speed along the ground, which may be curving round an asteroid.
      const walk = Math.abs(vDot(me.velocity, rightVector(me.up)));
      if (me.grounded && walk > 1) {
        this.stepDistance += walk * dt;
        if (this.stepDistance > STRIDE) {
          this.stepDistance -= STRIDE;
          this.leftFoot = !this.leftFoot;
          // Each footfall a little different, and sounding like what you walk on.
          const rate = (this.leftFoot ? 1 : 1.06) + (Math.random() - 0.5) * 0.08;
          this.sfx.play(materialSound("step", materialOf(underFoot(me, sim))), { gain: 0.8 + Math.random() * 0.3, pan: 0 }, rate);
        }
      } else this.stepDistance = STRIDE * 0.6; // the first step after landing or stopping comes soon
    }
    this.surroundings(me, sim, dt);
    return hurt;
  }

  /** Badly hurt, your heart pounds: quicker the closer you are to nothing. */
  private heartbeat(me: PlayerState | undefined, dt: number): void {
    const low = HEALTH.max * HEARTBEAT_BELOW;
    if (!me || me.health <= 0 || me.health >= low) {
      this.heartIn = 0;
      return;
    }
    this.heartIn -= dt;
    if (this.heartIn > 0) return;
    const left = me.health / low;
    this.heartIn = HEARTBEAT_EVERY * (0.6 + 0.4 * left);
    this.sfx.play("heartbeat", { gain: 1 - 0.3 * left, pan: 0 });
  }

  /**
   * Near an old living rock with its heart still in it, you hear the heart:
   * slow beats, louder the closer you get to it (digging toward it).
   */
  private rockHeart(me: PlayerState | undefined, sim: Sim | undefined, dt: number): void {
    this.rockHeartIn -= dt;
    if (!me || !sim || this.rockHeartIn > 0) return;
    this.rockHeartIn = ROCK_HEART_EVERY;
    let best = Infinity;
    for (const a of sim.asteroids) {
      const i = a.def.dead ? null : heartIndex(a.def);
      if (i === null || a.grid.tiles[i] !== T.HEART) continue;
      best = Math.min(best, Math.hypot(me.position.x - (a.def.ox + (i % a.grid.w) + 0.5), me.position.y - (a.def.oy + Math.floor(i / a.grid.w) + 0.5)));
    }
    const near = 1 - best / ROCK_HEART_REACH;
    if (near > 0) this.sfx.play("heartbeat", { gain: 0.25 + 0.6 * near, pan: 0 }, 0.7);
  }

  /** The sound of the place we're in: timbers creaking aboard, a sun's hum, water lapping, wind on the home rock. */
  private surroundings(me: PlayerState | undefined, sim: Sim | undefined, dt: number): void {
    const ship = me?.frame ? sim?.ships.get(me.frame) : undefined;
    let creak = 0;
    if (ship) {
      // A timber ship creaks a little at rest, more as it moves and most as it turns.
      const timber = this.timberFraction(ship);
      if (timber > 0.5) {
        const speed = Math.hypot(ship.state.velocity.x, ship.state.velocity.y);
        const strain = Math.min(1, speed / 40 + Math.abs(ship.state.angularVelocity) * 1.5);
        creak = timber * (0.06 + 0.3 * strain);
      }
    }
    this.probeIn -= dt;
    if (!me || !sim) this.place = { hum: 0, lap: 0, wind: 0 };
    else if (this.probeIn <= 0) {
      this.probeIn = PROBE_EVERY;
      this.place = lookAround(me, sim);
    }
    this.sfx.loop("creak", creak, dt);
    this.sfx.loop("hum", this.place.hum, dt);
    this.sfx.loop("lap", this.place.lap, dt);
    this.sfx.loop("wind", this.place.wind, dt);
  }

  /** How much of a ship's blocks are timber, recounted only when it gains or loses some. */
  private timberFraction(ship: ShipBody): number {
    const cells = ship.stats.cells;
    const known = this.timber.get(ship.state.id);
    if (known && known.cells === cells.length) return known.fraction;
    let wood = 0;
    for (const i of cells) if (materialOf(ship.grid.tiles[i]!) === "wood") wood++;
    const fraction = cells.length ? wood / cells.length : 0;
    // Ships come and go; don't keep every one ever boarded.
    if (this.timber.size > 64) this.timber.clear();
    this.timber.set(ship.state.id, { cells: cells.length, fraction });
    return fraction;
  }
}

/** Where round a character to feel for water: the middle and a ring a few blocks out. */
const AROUND: Vec2[] = [
  { x: 0, y: 0 },
  ...Array.from({ length: 8 }, (_, i) => ({ x: Math.cos((i * Math.PI) / 4) * 4, y: Math.sin((i * Math.PI) / 4) * 4 })),
];

/** How loud each of the place's loops should be where `me` is. */
function lookAround(me: PlayerState, sim: Sim): { hum: number; lap: number; wind: number } {
  const c = playerWorldCenter(me, sim);
  // The nearest sun hums, louder the closer to its surface.
  let near = 0;
  for (const sun of sim.world.suns) near = Math.max(near, 1 - Math.max(0, vDist(c, sun.position) - sun.radius) / HUM_RANGE);
  const hum = near * near * 0.3;
  // Water: in it, or just by it.
  let wet = 0;
  for (const o of AROUND) if (waterAt(sim, { x: c.x + o.x, y: c.y + o.y })) wet++;
  const lap = waterAt(sim, c) ? 0.3 : (wet / AROUND.length) * 0.35;
  // Wind: on foot on the surface of the home asteroid, not down in its caves.
  const field = me.frame ? undefined : gravityFieldAt(sim, c);
  const onSurface = field?.def.kind === "home" && vDist(c, asteroidCenter(field.def)) > field.def.radius * 0.85;
  return { hum, lap, wind: onSurface ? 0.16 : 0 };
}

/** The block under a character's feet (whatever ship or asteroid they're on). */
function underFoot(p: PlayerState, sim: Sim): TileId {
  const feet = playerWorldPos(p, sim);
  const down = upVector(playerWorldUp(p, sim));
  const target = editTargetAt(sim, feet.x - down.x * 0.3, feet.y - down.y * 0.3, p.frame);
  return editTargetTile(sim, target);
}
